import { FC, ReactNode } from 'react';
import BottomBarWrapper from '@campaigns/src/components/bottom-bar/BottomBarWrapper';
import { Button, Icon, Flex } from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { FLOWS_PRODUCTS_PATH } from '@flows/src/FlowsProductsRouter';
import BottomBarBasketActions from '@flows/src/components/flow-products/quotes/basket/BottomBarBasketActions';
import BottomBarSelectionOverviewActions from '@flows/src/components/flow-products/quotes/basket/BottomBarSelectionOverviewActions';
import { useYellowhiveQuotesWithPolling } from '@flows/src/hooks/flow-products/useYellowhiveQuotesWithPolling';
import useYellowhiveFlowProductSelectionStore from '@flows/src/store/zustand/flows-products/useYellowhiveFlowProductSelectionStore';
import { isCampaignsUrl } from '../../../../app/src/features/authentication/constants/SocialAuth';
import { useFlowProductsSidebarNavigation } from '@flows/src/hooks/flow-products/useFlowProductsSidebarNavigation';

interface Props {
  handlePrevious?: () => void;
  backText?: string;
}

const YellowhiveFlowProductsBottomBar: FC<Props> = ({
  handlePrevious,
  backText,
}) => {
  const { t } = useTranslation();
  const { flowId } = useParams<{ flowId: string }>();
  const history = useHistory();
  const queryParams = new URLSearchParams(window.location.search);
  const sessionId = queryParams.get('session_id') || undefined;
  const yellowhiveQuotesData = useYellowhiveQuotesWithPolling({
    sessionId,
    enabled: !!sessionId,
  });

  const { getNextProductUrl, getPreviousProductUrl, isFirstTag, isLastTag } =
    useFlowProductsSidebarNavigation(yellowhiveQuotesData?.data?.navigationMap);

  const isSelectionOverviewPage =
    window.location.pathname.includes('selection-overview');
  const isBasketPage = window.location.pathname.includes('basket');
  const isBasketEmpty = useYellowhiveFlowProductSelectionStore(
    (state) => !(state.selectedYellowhiveItems.length > 0),
  );
  const isRecalculating = useYellowhiveFlowProductSelectionStore(
    (state) => state.recalculatingProductIds.size > 0,
  );

  // Navigate back to dashboard when on selection overview page
  const handleDashboardNavigation = () => {
    history.push('/dashboard');
  };

  const handleNavigation = () => {
    history.push({
      pathname: `${FLOWS_PRODUCTS_PATH}/${flowId}/basket`,
      search: window.location.search,
    });
  };

  const handleNextCategoryNavigation = () => {
    const nextUrl = getNextProductUrl();
    if (nextUrl) {
      history.push(nextUrl);
    } else {
      // Fallback to basket navigation when no further categories are available
      handleNavigation();
    }
  };

  const handlePrevCategoryNavigation = () => {
    const prevUrl = getPreviousProductUrl();
    if (prevUrl) {
      history.push(prevUrl);
    }
  };

  const isFirstCategory = isFirstTag();
  const isLastCategory = isLastTag();

  // Compose left/back button
  let leftButton: ReactNode = null;

  if (isSelectionOverviewPage) {
    // Handled separately further down (Back to dashboard)
  } else if (isBasketPage) {
    // Keep original behaviour for basket page
    leftButton = (
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore TS2590: Expression produces a union type that is too complex to represent.
      <Button
        flexShrink={0}
        onClick={handlePrevious}
        leftIcon={<Icon name="GeneralActionsArrowLeft" />}
        variant="white"
      >
        {backText}
      </Button>
    );
  } else {
    // Overview page – cycle through categories or go back to questions
    const label = isFirstCategory ? backText : t('PREVIOUS');
    const onClick = isFirstCategory
      ? handlePrevious
      : handlePrevCategoryNavigation;

    leftButton = (
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore TS2590: Expression produces a union type that is too complex to represent.
      <Button
        flexShrink={0}
        onClick={onClick}
        leftIcon={<Icon name="GeneralActionsArrowLeft" />}
        variant="white"
      >
        {label}
      </Button>
    );
  }

  // Compose primary (right) action button for overview page
  let rightButton: ReactNode = null;

  if (!isBasketPage && !isSelectionOverviewPage) {
    const disableButton =
      isRecalculating || (isLastCategory ? isBasketEmpty : false);
    const onClick = isLastCategory
      ? handleNavigation
      : handleNextCategoryNavigation;
    const labelKey = isLastCategory
      ? 'CAMPAIGNS_DETAIL-CHOSEN_PRODUCTS'
      : 'NEXT';

    rightButton = (
      <Button
        flexShrink={0}
        ml="auto"
        onClick={onClick}
        isDisabled={disableButton}
        rightIcon={<Icon name="GeneralActionsArrowRight" />}
      >
        {t(labelKey)}
      </Button>
    );
  }

  return (
    <BottomBarWrapper>
      {/* Mobile Layout - Sticky Footer */}
      <Flex
        display={{ base: 'flex', tablet: 'none' }}
        w="100%"
        justify="space-between"
        align="center"
        gap="3"
      >
        {/* Left Button - Back */}
        {!isBasketPage && !isSelectionOverviewPage && (
          <Button
            flexShrink={0}
            onClick={
              isFirstCategory ? handlePrevious : handlePrevCategoryNavigation
            }
            leftIcon={<Icon name="GeneralActionsArrowLeft" />}
            variant="outline"
            size="md"
            h="48px"
            px="4"
            borderRadius="md"
            fontSize="sm"
          >
            {t('PREVIOUS')}
          </Button>
        )}

        {isSelectionOverviewPage && !isCampaignsUrl && (
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore TS2590: Expression produces a union type that is too complex to represent.
          <Button
            flexShrink={0}
            onClick={handleDashboardNavigation}
            leftIcon={<Icon name="GeneralActionsArrowLeft" />}
            variant="outline"
            size="md"
            h="48px"
            px="4"
            borderRadius="md"
            fontSize="sm"
          >
            {t('BACK-TO-DASHBOARD')}
          </Button>
        )}

        {isBasketPage && (
          <Button
            flex="1"
            onClick={handlePrevious}
            leftIcon={<Icon name="GeneralActionsArrowLeft" />}
            variant="outline"
            size="md"
            h="48px"
            borderRadius="md"
            maxW="200px"
            px="6"
            fontSize="sm"
          >
            {backText}
          </Button>
        )}

        {/* Right Button - Primary Action */}
        {!isBasketPage && !isSelectionOverviewPage && (
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore TS2590: Expression produces a union type that is too complex to represent.
          <Button
            flexGrow={1}
            onClick={
              isLastCategory ? handleNavigation : handleNextCategoryNavigation
            }
            isDisabled={
              isRecalculating || (isLastCategory ? isBasketEmpty : false)
            }
            rightIcon={<Icon name="GeneralActionsArrowRight" />}
            variant="solid"
            colorScheme="primary"
            size="md"
            h="48px"
            borderRadius="md"
            fontWeight="600"
            fontSize="sm"
          >
            {t(isLastCategory ? 'CAMPAIGNS_DETAIL-CHOSEN_PRODUCTS' : 'NEXT')}
          </Button>
        )}
        {isSelectionOverviewPage && (
          // Primary proceed button on mobile footer for selection overview
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore TS2590: Expression produces a union type that is too complex to represent.
          <Button
            flexGrow={1}
            onClick={() => {
              // Start contract flow via deeplink handling in BottomBarSelectionOverviewActions by navigating with ?deeplink=slotvragen
              const url = new URL(window.location.href);
              const params = new URLSearchParams(url.search);
              params.set('deeplink', 'slotvragen');
              window.location.href = `${url.pathname}?${params.toString()}`;
            }}
            rightIcon={<Icon name="GeneralActionsArrowRight" />}
            variant="solid"
            colorScheme="primary"
            size="md"
            h="48px"
            borderRadius="md"
            fontWeight="600"
            fontSize="sm"
          >
            {t('FLOWS_PRODUCTS_SELECTION-OVERVIEW_ACTION')}
          </Button>
        )}
      </Flex>

      {/* Desktop Layout */}
      <Flex
        display={{ base: 'none', tablet: 'flex' }}
        w="100%"
        justify="space-between"
        align="center"
      >
        {isSelectionOverviewPage && !isCampaignsUrl && (
          <Button
            flexShrink={0}
            onClick={handleDashboardNavigation}
            leftIcon={<Icon name="GeneralActionsArrowLeft" />}
            variant="white"
          >
            {t('BACK-TO-DASHBOARD')}
          </Button>
        )}
        {leftButton}
        {rightButton}
      </Flex>

      {isBasketPage && <BottomBarBasketActions />}
      {isSelectionOverviewPage && (
        <>
          {/* Desktop: keep actions in footer (email + proceed). Email is hidden in mobile footer and rendered in content instead. */}
          <Flex display={{ base: 'none', tablet: 'flex' }} w="100%">
            <BottomBarSelectionOverviewActions flowId={flowId} />
          </Flex>
        </>
      )}
    </BottomBarWrapper>
  );
};

export default YellowhiveFlowProductsBottomBar;
